import { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import { pressable } from '@aytm/app/stats/components/lib/pressable'
import HeatmapPopup from '@aytm/app/stats/page/question-views/hm/components/chart/components/action-buttons/HeatmapPopup'
import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'

import { EXPORT_OPTIONS } from '@aytm/app/stats/page/question-views/hm/components/chart/lib/constants'
import {
  buildSvgForPopup,
  buildSvgForExport,
  loadImageAsBase64
} from '@aytm/app/stats/page/question-views/hm/components/chart/components/action-buttons/utils'

import style from '@aytm/app/stats/page/question-views/hm/components/chart/components/action-buttons/styles/ActionButtons.module.scss'
import { canvasToFile, svgToCanvas } from '@aytm/app/stats/lib/render/svg-export'

const ActionButtons = ({
  id,
  image,
  viewMode,
  svgContainerRef,
  showClusters,
  imageWidth,
  imageHeight,
  questionKey,
  apiRef
}) => {
  const [imgBase64, setImgBase64] = useState(null)
  const [popup, setPopup] = useState(null)
  // null until the artwork has been asked for; false once it came back unreadable
  const [imageAvailable, setImageAvailable] = useState(null)

  const handleExport = useCallback(
    async (fileType, callback) => {
      try {
        const svgNode = buildSvgForExport(svgContainerRef.current, {
          id,
          viewMode,
          imgBase64,
          imageWidth,
          imageHeight,
          showClusters
        })

        // no composite to write (missing artwork or overlay) -- still release the dropdown spinner
        if (!svgNode) {
          callback()
          return
        }

        const svgString =
          '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svgNode)

        // TODO: replace when page.lib will be moved to React
        // @ts-ignore
        const canvas = await svgToCanvas(svgString, imageWidth, imageHeight)

        // @ts-ignore
        await canvasToFile(canvas, fileType, questionKey, 'export_heatmap')

        callback()
      } catch (error) {
        console.error(error) // eslint-disable-line no-console
        callback()
      }
    },
    [id, viewMode, imgBase64, imageWidth, imageHeight, showClusters, svgContainerRef, questionKey]
  )

  // Live-page PPTX bridge (pptx.coffee -> block.getCanvasFromSVG). This component owns both
  // halves the composite needs — the rendered svg container and the base64 image — so it hands
  // the builder up rather than having the block re-derive them. Reassigned every render so the
  // closure sees the current view/base64; apiRef identity stays stable for onChartReady.
  if (apiRef) {
    // async: the base64 is loaded lazily, and the PPTX export can fire before that state
    // has settled — legacy took an imgBase64 argument for the same reason. Resolve it here
    // rather than returning an empty composite.
    apiRef.current.getExportSvg = async () => {
      const base64 = imgBase64 || (image?.original?.path ? await loadImageAsBase64(image.original.path) : null)
      if (!base64) return null

      const svgNode = buildSvgForExport(svgContainerRef.current, {
        id,
        viewMode,
        imgBase64: base64,
        imageWidth,
        imageHeight,
        showClusters
      })

      if (!svgNode) return null

      return '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svgNode)
    }
    apiRef.current.getExportSize = () => ({ width: imageWidth, height: imageHeight })
  }

  useEffect(() => {
    if (!image?.original?.path) return

    // aborted on unmount so a torn-down fetch is not read as a missing artwork
    const controller = new AbortController()
    loadImageAsBase64(image.original.path, { signal: controller.signal })
      .then(base64 => {
        setImgBase64(base64)
        setImageAvailable(null != base64)
      })
      .catch(() => {}) // only the teardown AbortError rejects; real failures resolve null
    return () => controller.abort()
  }, [image?.original?.path])

  // Headless export: reuse the exact composite builder the live Download button uses.
  // imgBase64 comes from loadImageAsBase64 above (BE sends only the image URL/path).
  // No-op on the live page (no ExportContext); on the render route it rasterizes the
  // composite via the ported svgToPngDataUrl instead of the page bridge.
  useExportableChart({
    deps: [imgBase64],
    prepareForExport: async () => {
      const svgNode = buildSvgForExport(svgContainerRef.current, {
        id,
        viewMode,
        imgBase64,
        imageWidth,
        imageHeight,
        showClusters
      })

      if (!svgNode) throw new Error('Heatmap export: composite build returned null (missing overlay svg or base64)')

      const svgString =
        '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(svgNode)

      return [await svgToPngDataUrl(svgString, imageWidth, imageHeight)]
    }
  })

  // Both controls compose the artwork with the overlay, so neither can produce a correct result
  // once it is gone -- they stay in place and read as unavailable rather than writing a blank file.
  const artworkMissing = false === imageAvailable
  const unavailableTitle = 'Image unavailable'
  // The export composes from the base64 copy, which arrives after the picture is drawn; until then a
  // press would have nothing to write. The zoom draws from the picture's own URL and stays live.
  const artworkLoading = null === imageAvailable && Boolean(image?.original?.path)

  const handleZoomClick = useCallback(() => {
    if (artworkMissing) return

    const svgNode = buildSvgForPopup(svgContainerRef.current, viewMode, showClusters)
    if (!svgNode) return

    setPopup({
      src: image.original.url,
      svgContent: svgNode.innerHTML,
      imgOriginalWidth: imageWidth,
      imgOriginalHeight: imageHeight
    })
  }, [artworkMissing, svgContainerRef, image, imageWidth, imageHeight, viewMode, showClusters])

  return (
    <>
      <div className={style.buttons}>
        <i
          className={classNames('icon-aytmfd-zoom', style.zoom, { [style.disabled]: artworkMissing })}
          aria-label="Zoom"
          title={artworkMissing ? unavailableTitle : undefined}
          {...pressable(handleZoomClick)}
        />
        <ExportDropdown
          options={EXPORT_OPTIONS}
          onExport={handleExport}
          disabled={artworkMissing || artworkLoading}
          tooltip={artworkMissing ? unavailableTitle : artworkLoading ? 'Image loading...' : 'Export'}
        />
      </div>

      {popup && (
        <HeatmapPopup
          src={popup.src}
          svgContent={popup.svgContent}
          imgOriginalWidth={popup.imgOriginalWidth}
          imgOriginalHeight={popup.imgOriginalHeight}
          show
          showImg
          scrolling={false}
          closeOnClick={true}
          onClose={() => setPopup(null)}
        />
      )}
    </>
  )
}

ActionButtons.propTypes = {
  id: PropTypes.string,
  image: PropTypes.object,
  viewMode: PropTypes.string,
  svgContainerRef: PropTypes.object,
  showClusters: PropTypes.bool,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  questionKey: PropTypes.string,
  apiRef: PropTypes.object
}

export default ActionButtons
